import cx from "clsx";
import SizingContainer from "common-utils/components/SizingContainer";
import { getAspectRatioFromFile } from "common-utils/components/SizingContainer/components/AutoHeight/utils";
import { GalleryOffer } from "kreate-common/modules/business-types";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { safeParseKreateUtxoOffer } from "kreate-common/modules/utxo-parser/by-type/offer";
import React from "react";

import { runFlow$CancelOffer } from "../../../PageGalleryArtwork/containers/ArtworkDetailsViewer/generators/runFlow$CancelOffer";
import { getDisplayNameForGalleryArtwork } from "../../../PageGalleryCollector/utils";

import styles from "./index.module.scss";
import { createContainer } from "./utils";

import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import { EM_DASH } from "@/modules/common-utils/unicode";
import Format from "@/modules/kreate-components/components/Format";
import SecondaryFileViewer from "@/modules/kreate-components/components/SecondaryFileViewer";
import UserDisplayNameViewerV2 from "@/modules/kreate-components/components/UserDisplayNameViewerV2";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";

const Layout = {
  Article: createContainer("article", styles.article),
  Left: createContainer("div", styles.left),
  Content: createContainer("div", styles.content),
  Title: createContainer("h3", styles.title),
  Details: createContainer("div", styles.details),
  Actions: createContainer("div", styles.actions),
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
  offer: GalleryOffer;
  isLoggedInUserGalleryOwner: boolean;
};

export default function SentOfferViewer({
  className,
  style,
  offer,
  isLoggedInUserGalleryOwner,
}: Props) {
  const modalTriggers = {
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler((error) =>
    modalTriggers.ModalFailure.openModalWithError(error)
  );

  const handleCancel = withErrorHandler(async () => {
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$CancelOffer({
        cip30Wallet,
        offer,
        txMessage: `Offer for『${getDisplayNameForGalleryArtwork(
          offer.populated?.galleryArtwork
        )}』was just cancelled!`,
      })
    );
  });

  const datumOffer = safeParseKreateUtxoOffer(offer.utxo).result?.datumOffer;

  return (
    <Layout.Article className={cx(styles.article, className)} style={style}>
      <Layout.Left>
        <SizingContainer.AutoHeight
          className={styles.imageContainer}
          aspectRatio={
            getAspectRatioFromFile(offer.populated?.galleryArtwork?.file) ||
            1 / 1
          }
          minAspectRatio={Math.exp(-1 / 2)}
          maxAspectRatio={Math.exp(+1 / 2)}
        >
          <SecondaryFileViewer
            file={offer.populated?.galleryArtwork?.file}
            thumbnailFile={offer.populated?.galleryArtwork?.imageFile}
            sizes="124px"
            fill
          />
        </SizingContainer.AutoHeight>
      </Layout.Left>
      <Layout.Content>
        <Layout.Title
          content={offer.populated?.galleryArtwork?.title || EM_DASH}
        />
        <Layout.Details>
          <Flex.Col width="100%" minWidth="0" gap="4px">
            <Flex.Cell>
              <Format.LovelaceAmount
                lovelaceAmount={datumOffer?.editionPrice}
                unstyled
              />
              <span>{", "}</span>
              <Format.Integer integer={datumOffer?.wantedQuantity} unstyled />
              <span>{" left"}</span>
            </Flex.Cell>
            <Flex.Row className={styles.fontSerif} gap="0.25em" width="100%">
              <Flex.Cell flex="0 0 auto" asChild>
                <em>{"Artist: "}</em>
              </Flex.Cell>
              <Flex.Cell flex="0 1 auto" minWidth="0" asChild>
                <UserDisplayNameViewerV2
                  user={
                    offer.populated?.galleryArtwork?.kreateArtwork?.populated
                      ?.createdBy
                  }
                  unstyled
                />
              </Flex.Cell>
            </Flex.Row>
          </Flex.Col>
        </Layout.Details>
        {isLoggedInUserGalleryOwner ? (
          <Layout.Actions>
            <ButtonSecondary
              content="Cancel"
              onClick={handleCancel}
              disabled={busy}
            />
          </Layout.Actions>
        ) : undefined}
      </Layout.Content>
    </Layout.Article>
  );
}
